import Button from 'components/base/Button';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { Col, FloatingLabel, Form, Row, Toast, ToastContainer } from 'react-bootstrap';

import { useAuth } from 'providers/AuthProvider'
import { useState, useEffect, FormEvent } from 'react';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import CertificateForm from './components/CertificateForm';
import Dropzone from 'components/base/Dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


const UpdateCertificate = () => {

    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('Feedback'),
            url: '/feedback-dashboard'
        },
        {
            label: ('Certificates'),
            url: '/feedback-dashboard/certificates'
        },
        {
            label: t('Edit a certificate'),
            active: true
        }
    ];

    const navigate = useNavigate();
    const auth = useAuth();

    // Route parameters
    const { certificate_id } = useParams();

    // State to store the form data from all child components
    const [formData, setFormData] = useState<any>({ 'certificateForm': '', 'notes': '', 'files': null });

    // State to store existing contact data before update
    const [initialData, setInitialData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [files, setFiles] = useState<File[]>([]);

    // already uploaded file (resource)
    const [fileUrl, setFileUrl] = useState('');
    const [fileName, setFileName] = useState('');
    const [resourceId, setResourceId] = useState(0);

    // Function to handle input changes
    const handleNotesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Function to handle form data update from child components
    const updateFormData = (name: string, data: any) => {
        setFormData({ ...formData, [name]: data });
    };

    const handleSumbit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = new FormData();
        form.append('certificateForm', JSON.stringify(formData['certificateForm']));
        form.append('notes', formData['notes']);
        form.append('existing_file', resourceId ? resourceId + '' : '0');

        // new file(s)
        files.forEach(file => {
            form.append('files', file);
        });

        try {
            const response = await apiClient.put(
                `/v1/certificates/${certificate_id}`, form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShow(true);
        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        }

    };

    const fetchCertificate = async () => {
        try {
            const response = await apiClient.get(
                `/v1/certificates/${certificate_id}`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            const obj = {
                'certificate_from': response.data['certificate_from'],
                'certificate_title': response.data['certificate_title'],
                'certificate_date': response.data['certificate_date']
            };

            setResourceId(response.data['resource_id']);
            setFileUrl(response.data['resource_url']);
            setFileName(response.data['resource_name']);

            setInitialData(obj);
            formData.certificateForm = obj;
            formData.notes = response.data['notes'];

        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCertificate();
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="mb-9">
            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => { setShow(false); navigate(-1); }} show={show} delay={3000} autohide>
                        <Toast.Body className=''>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>

            <PageBreadcrumb items={breadcrumbItems} />
            <Form onSubmit={handleSumbit}>
                <div className="border-bottom mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
                    <Row>
                        <Col xl={12}>
                            <div className="d-sm-flex justify-content-between">
                                <h2 className="mb-4">{t('UpdateCertificate.edit_certificate')}</h2>
                                <div className="d-flex mb-3">
                                    <Button variant="phoenix-primary" className="me-2 px-6" onClick={() => navigate(-1)} >
                                        {t('Back')}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                <Row>
                    <Col sm={8} md={8} >
                        <CertificateForm initialData={initialData} onUpdate={data => updateFormData('certificateForm', data)} className="mb-6" />
                    </Col>
                    <Col>
                        <div className="mb-5">

                            {resourceId > 0 ? (
                                <>
                                    <h4 className="mb-3">{t('UpdateCertificate.available_document')}</h4>
                                    <div className="d-flex bg-body-secondary">
                                        <div className="p-2 flex-grow-1 bg-body-secondary border-bottom">
                                            <a href={`${fileUrl}`} target="_blank" rel="noopener noreferrer">
                                                {fileName}
                                            </a>
                                        </div>
                                        <div className="p-2 bg-body-secondary border-bottom">
                                            <FontAwesomeIcon icon={faTrashCan} className="text-danger fs-8 me-1" onClick={() => setResourceId(-1)} />
                                        </div>

                                    </div>
                                </>
                            ) : (
                                <>
                                    <h4 className="mb-3">{t('UpdateCertificate.add_document')}</h4>
                                    <Dropzone single={true}
                                        className="mb-3"
                                        onFileListUpdate={files => setFiles(files)}
                                        accept={{
                                            'image/*': ['.png', '.gif', '.jpeg', '.jpg'],
                                            'application/pdf': ['.pdf'],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </Col>
                </Row><Row>
                    <Col>

                        <h4 className="mb-3">{t('UpdateCertificate.certificate_notes')}</h4>
                        <FloatingLabel
                            controlId="certificates-notes"
                            label={t('UpdateCertificate.notes')}
                        >
                            <Form.Control
                                as="textarea"
                                style={{ height: '128px' }}
                                type="text"
                                name='notes' value={formData.notes} onChange={handleNotesInputChange} />
                        </FloatingLabel>
                        <div className="text-end">
                            <Button type="submit" variant="primary" className='mt-2'>{t('UpdateCertificate.save')}</Button>
                        </div>

                    </Col>
                </Row>
            </Form>
        </div>
    );

};

export default UpdateCertificate;
